<script setup lang="ts">
import type { Sidebar } from '@/types/global.types'
import Icon from '@/components/shared/Icon.vue'
import { hasAccess } from '@/lib'
import { getThemeSettings } from '@/lib'
import { useEventListener, useStorage } from '@vueuse/core'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

defineProps<{
  visible: boolean
}>()

const {
  sidebar,
  logoHref = '/',
  logo,
  logoMargin = '0px',
  appTitle,
  IdentityPanelHost,
} = getThemeSettings()

const expandedItems = ref<Set<string>>(new Set())
const fullPath = ref<string>('')
const hasMounted = ref(false)

const sidebarWidth = useStorage('global-sidebar-width', 260)
const sidebarAnchor = ref<HTMLElement | null>(null)
const dragAnchor = ref<HTMLElement | null>(null)

const isResizing = ref(false)
const startX = ref(0)
const startWidth = ref(0)

const route = useRoute()
const router = useRouter()

const minSidebarWidth = 260
const maxSidebarWidth = 400

const sidebarLinks = [
  {
    Type: 'UrlSidebarLink',
    Name: 'Dashboard',
    Label: 'Dashboard',
    Icon: 'home',
    Link: '/',
    Audiences: ['Role: Everyone'],
  } as Sidebar,
  ...sidebar,
]

const logoHrefElement = computed(() => (isRelativeUrl(logoHref) ? 'router-link' : 'a'))

function isRelativeUrl(url: string): boolean {
  return /^\/(?!\/)/.test(url)
}

function removeBasePath(url: string): string {
  const formIndex = url.indexOf('/form/')
  return formIndex !== -1 ? url.substring(formIndex) : url
}

function findParentByLink(root: Sidebar[]): Sidebar | null {
  function search(children: Sidebar[], parent: Sidebar | null): Sidebar | null {
    for (const child of children) {
      if (child.Link === route.path || child.Link === window.location.href) return parent

      if (child.Children) {
        const found = search(child.Children, child)
        if (found) return found
      }
    }
    return null
  }

  return search(root, null)
}

function handleClick(item: Sidebar, event: MouseEvent) {
  if (item.Children) {
    expandedItems.value.has(item.Label)
      ? expandedItems.value.delete(item.Label)
      : expandedItems.value.add(item.Label)
  } else {
    const isNewTabClick = event.metaKey || event.ctrlKey

    if (isNewTabClick) {
      // Open in new tab for command/ctrl+click
      if (!isRelativeUrl(item.Link) && !item.Link.includes('form')) {
        window.open(item.Link, '_blank')
      } else {
        const link = isRelativeUrl(item.Link) ? item.Link : removeBasePath(item.Link)
        const fullUrl = new URL(link, window.location.origin).href
        window.open(fullUrl, '_blank')
      }
    } else {
      // Normal click behavior
      if (!isRelativeUrl(item.Link) && !item.Link.includes('form')) {
        window.location.href = item.Link
        return
      }

      const link = isRelativeUrl(item.Link) ? item.Link : removeBasePath(item.Link)
      router.push(link)
    }
  }
}

watch(route, () => {
  fullPath.value = window.location.href

  if (!hasMounted.value) {
    hasMounted.value = true
    const item = findParentByLink(sidebar)

    if (item?.Label && !expandedItems.value.size) expandedItems.value.add(item.Label)
  }
})

function startResizing(e: MouseEvent) {
  if (!dragAnchor.value?.contains(e.target as Node)) return

  isResizing.value = true
  startX.value = e.clientX
  startWidth.value = sidebarWidth.value

  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'

  // Prevent text selection during resize
  e.preventDefault()
}

function stopResizing() {
  if (!isResizing.value) return

  isResizing.value = false
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
}

function resize(e: MouseEvent) {
  if (!isResizing.value) return

  const delta = e.clientX - startX.value
  const newWidth = Math.min(Math.max(startWidth.value + delta, minSidebarWidth), maxSidebarWidth)

  sidebarWidth.value = newWidth
  document.body.style.setProperty('--sidebar-width', `${newWidth}px`)
}

// Use passive: false for better touch device support
useEventListener(document, 'mousedown', startResizing, { passive: false })
useEventListener(document, 'mouseup', stopResizing)
useEventListener(document, 'mousemove', resize)
useEventListener(document, 'mouseleave', stopResizing)

onMounted(() => {
  document.body.style.setProperty('--sidebar-width', `${sidebarWidth.value}px`)
})
</script>

<template>
  <aside
    ref="sidebarAnchor"
    :class="{ 'transition-all duration-[250ms]': !isResizing }"
    class="flex overflow-hidden absolute inset-y-0 z-20 flex-col h-full select-none primary-gradient dark:bg-gray-800 dark:border-gray-700 dark:border-r shrink-0 lg:relative lg:z-0"
    :style="{
      width: visible ? `${sidebarWidth}px` : '0px',
      opacity: visible ? '1' : '0',
      visibility: visible ? 'visible' : 'hidden',
    }"
  >
    <div
      v-if="visible"
      ref="dragAnchor"
      class="absolute inset-y-0 right-0 z-10 w-4 opacity-0 transition-opacity transform translate-x-1/2 hover:opacity-100 cursor-col-resize"
      :class="{ 'opacity-100': isResizing }"
    >
      <div class="w-[3px] left-1/2 transform -translate-x-1/2 bg-accent-500 absolute inset-y-0" />
    </div>
    <nav style="flex: 1 0 auto" class="flex overflow-hidden flex-col flex-1 justify-between h-full">
      <div>
        <component
          :is="logoHrefElement"
          :to="logoHref"
          :href="logoHref"
          class="flex gap-x-3.5 items-center px-6 pt-6 pb-5"
          tabindex="-1"
        >
          <img
            class="h-8 w-auto max-w-[212px]"
            :src="logo"
            alt="Company Logo"
            :style="{ margin: logoMargin }"
          />
          <h1 v-if="appTitle" class="text-xl font-medium text-white">
            {{ appTitle }}
          </h1>
        </component>
        <ul class="px-3">
          <li v-for="item in sidebarLinks" :key="item.Label">
            <button
              type="button"
              class="flex gap-x-3 justify-between items-center px-4 py-1.5 w-full text-sm font-medium text-white truncate rounded-md hover:bg-white/10 focus:ring-inset focus:ring-2 focus:ring-white/50 focus:outline-none"
              :class="{
                'bg-white/20 hover:bg-white/20': route.path === item.Link || fullPath === item.Link,
              }"
              @click="(event) => handleClick(item, event)"
            >
              <div class="flex gap-x-3 items-center">
                <Icon
                  v-if="item.Icon"
                  :variant="item.Icon"
                  class="w-5 h-5 text-white/90 shrink-0"
                />
                <span>{{ item.Label }}</span>
              </div>
              <Icon
                v-if="item.Children"
                variant="chevron-down"
                class="w-5 h-5 text-white/90 shrink-0"
              />
            </button>

            <ul
              v-if="item.Children"
              class="overflow-hidden"
              :style="{ height: expandedItems.has(item.Label) ? 'auto' : '0px' }"
            >
              <li v-for="child in item.Children" :key="child.Label">
                <button
                  type="button"
                  class="flex gap-x-3 items-center py-1.5 pr-4 pl-7 w-full text-sm font-medium text-white truncate rounded-md hover:bg-white/10 focus:ring-inset focus:ring-2 focus:ring-white/50 focus:outline-none"
                  :class="{
                    'bg-white/20 hover:bg-white/20':
                      route.path === child.Link || fullPath === child.Link,
                  }"
                  :tabindex="expandedItems.has(item.Label) ? 0 : -1"
                  @click="(event) => handleClick(child, event)"
                >
                  <div class="flex gap-x-3 items-center">
                    <Icon
                      v-if="child.Icon"
                      :variant="child.Icon"
                      class="w-5 h-5 text-white/90 shrink-0"
                    />
                    <span>{{ child.Label }}</span>
                  </div>
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>
      <div
        v-if="hasAccess('Settings')"
        class="flex flex-col gap-y-2 px-6 py-4 border-t border-white/20"
      >
        <a
          class="flex justify-center items-center py-1.5 pr-4 pl-7 w-full text-sm font-medium text-white truncate rounded-md border hover:bg-white/10 focus:ring-inset focus:ring-2 focus:ring-white/50 focus:outline-none border-white/20"
          :href="`https://${IdentityPanelHost}/settings/servicepanel`"
        >
          <Icon variant="settings" class="w-[18px] h-[18px] shrink-0 mr-2" />
          <span>Settings</span>
        </a>
      </div>
    </nav>
  </aside>
</template>

<style scoped>
.transition-all {
  transition-property: width, opacity;
}
</style>
